/**
 * Saturday Delivery Surcharge Handler
 * Automatically adds/removes a €12 surcharge when Saturday delivery is selected
 *
 * This script listens to the Delivery & Pickup app's date selection
 * and manages a line item for the Saturday delivery surcharge.
 */

class SaturdayDeliveryHandler {
    constructor() {
        // Saturday surcharge variant ID (Samstagszustellung product)
        this.saturdaySurchargeVariantId = 51754369712466;

        this.surchargeAmount = 12.00;
        this.surchargeInCart = false;
        this.lastDetectedDay = null;

        this.init();
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    setup() {
        // Check if we're on the Samstagszustellung product page and hide the add-to-cart button
        this.hideSamstagszustellungCartButton();

        // Hide Samstagszustellung product in collections - DISABLED FOR NOW (using CSS instead)
        // this.hideSamstagszustellungInCollections();

        // Check if we're on the cart page
        const cartForm = document.querySelector('[data-cart-form]');
        const cart = document.querySelector('#cart');

        if (!cartForm && !cart) {
            return;
        }

        // Force Saturday delivery quantity to 1 max
        this.enforceSaturdayDeliveryQuantity();

        // Check if Saturday delivery is already in cart and show message
        this.updateMessageDisplay();

        // Listen for delivery date changes from the Delivery & Pickup app
        this.observeDeliveryDateChanges();

        // Also check on page load if there's already a selected date
        this.checkCurrentSelectedDate();
    }

    /**
     * Hide Samstagszustellung product in collections and search results
     */
    hideSamstagszustellungInCollections() {
        // Find all links to the Samstagszustellung product
        const samstagsLinks = document.querySelectorAll('a[href*="/products/samstagszustellung"]');

        samstagsLinks.forEach(link => {
            // Hide the product item (could be the link itself or a parent container)
            let productItem = link.closest('.product-item, .product-grid-item, .product-card, .collection-item, .grid-item');

            if (productItem) {
                productItem.style.display = 'none';
            } else {
                // If no parent found, hide the link itself
                link.style.display = 'none';
            }
        });
    }

    /**
     * Hide the add-to-cart button on the Samstagszustellung product page
     */
    hideSamstagszustellungCartButton() {
        // Check if we're on a product page
        const productForm = document.querySelector('product-form');
        if (!productForm) return;

        // Check if the variant ID matches Samstagszustellung
        const variantInput = productForm.querySelector('input[name="id"]');
        if (!variantInput) return;

        const variantId = parseInt(variantInput.value);

        // If this is the Samstagszustellung product, hide the cart button
        if (variantId === this.saturdaySurchargeVariantId) {
            document.body.classList.add('hide-samstagszustellung-cart');

            // Optionally add a notice message
            const productInfo = document.querySelector('.product-info, .product__content, .product-block');
            if (productInfo) {
                const notice = document.createElement('div');
                notice.className = 'samstagszustellung-notice';
                notice.innerHTML = '<strong>Hinweis:</strong> Dieser Zuschlag wird automatisch hinzugefügt, wenn Sie eine Samstagslieferung wählen.';

                // Insert after product title or at the beginning of product info
                const productTitle = productInfo.querySelector('.product__title, h1');
                if (productTitle) {
                    productTitle.insertAdjacentElement('afterend', notice);
                } else {
                    productInfo.insertBefore(notice, productInfo.firstChild);
                }
            }
        }
    }

    /**
     * Enforce Saturday delivery quantity to always be 1
     */
    async enforceSaturdayDeliveryQuantity() {
        // Check cart on page load
        await this.fixSaturdayDeliveryQuantity();

        // Listen for cart updates
        document.addEventListener('cart:updated', async () => {
            await this.fixSaturdayDeliveryQuantity();
        });

        // Listen for quantity changes
        document.addEventListener('change', async (e) => {
            const target = e.target;
            if (target.matches('input.qty-selector, input[type="number"][name="updates[]"]')) {
                await this.fixSaturdayDeliveryQuantity();
            }
        });
    }

    /**
     * Fix Saturday delivery quantity if it's more than 1
     */
    async fixSaturdayDeliveryQuantity() {
        try {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();

            const surchargeItems = cart.items.filter(item =>
                item.variant_id === this.saturdaySurchargeVariantId ||
                (item.properties && item.properties._samstagszustellung === 'true')
            );

            if (surchargeItems.length === 0) {
                return;
            }

            // If multiple items, keep only one and remove others
            if (surchargeItems.length > 1) {
                for (let i = 1; i < surchargeItems.length; i++) {
                    await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id: surchargeItems[i].key,
                            quantity: 0
                        })
                    });
                }
            }

            // If quantity is more than 1, set it to 1
            const firstItem = surchargeItems[0];
            if (firstItem.quantity > 1) {
                await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: firstItem.key,
                        quantity: 1
                    })
                });

                // Reload page to reflect changes
                window.location.reload();
            }

        } catch (error) {
            // Silent fail
        }
    }

    /**
     * Observe changes in the delivery date selector
     * The Delivery & Pickup app typically updates a hidden input or data attribute
     */
    observeDeliveryDateChanges() {
        // Method 1: Listen for custom events from the app
        document.addEventListener('delivery:dateChanged', (e) => {
            if (e.detail && e.detail.date) {
                this.handleDateChange(e.detail.date);
            }
        });

        // Method 2: Observe changes to the app container
        const appContainer = document.getElementById('delivery-pickup-app-container');
        if (appContainer) {
            const observer = new MutationObserver(() => {
                this.checkDeliveryAppDate();
            });

            observer.observe(appContainer, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['data-selected-date', 'value']
            });
        }

        // Method 3: Listen for input changes in the app container
        document.addEventListener('change', (e) => {
            const target = e.target;

            if (target.matches('#delivery-pickup-app-container input[type="date"], ' +
                               '#delivery-pickup-app-container input[name*="delivery"], ' +
                               '#delivery-pickup-app-container select[name*="delivery"]')) {
                this.handleDateChange(target.value);
            }
        }, true);

        // Method 4: Poll for changes (fallback)
        setInterval(() => {
            this.checkDeliveryAppDate();
        }, 2000);
    }

    /**
     * Check the current selected date in the Delivery & Pickup app
     */
    checkCurrentSelectedDate() {
        this.checkDeliveryAppDate();
    }

    /**
     * Check for date in the Delivery & Pickup app container
     */
    checkDeliveryAppDate() {
        const appContainer = document.getElementById('delivery-pickup-app-container');
        if (!appContainer) return;

        // Method 1: Check for "Liefertag" field (most reliable for Saturday detection)
        const liefertagInput = document.querySelector('input[name*="Liefertag"], input[name="properties[Liefertag]"]');
        if (liefertagInput && liefertagInput.value) {
            const dayName = liefertagInput.value.toLowerCase();

            // Only process if the day has changed
            if (dayName !== this.lastDetectedDay) {
                this.lastDetectedDay = dayName;

                if (dayName === 'samstag' || dayName === 'saturday') {
                    this.addSaturdaySurcharge();
                } else {
                    this.removeSaturdaySurcharge();
                }
            }
            return;
        }

        // Method 2: Check for "Liefertermin" field (DD/MM/YYYY format)
        const lieferterminInput = document.querySelector('input[name*="Liefertermin"], input[name="properties[Liefertermin]"]');
        if (lieferterminInput && lieferterminInput.value) {
            this.handleDateChange(lieferterminInput.value);
            return;
        }

        // Method 3: Try to find date input in app container
        const dateInput = appContainer.querySelector('input[type="date"], input[name*="delivery_date"]');
        if (dateInput && dateInput.value) {
            this.handleDateChange(dateInput.value);
            return;
        }

        // Method 4: Try to find date in data attributes
        const selectedDate = appContainer.dataset.selectedDate;
        if (selectedDate) {
            this.handleDateChange(selectedDate);
            return;
        }

        // Method 5: Try to find date in hidden inputs
        const hiddenDateInput = appContainer.querySelector('input[type="hidden"][name*="date"]');
        if (hiddenDateInput && hiddenDateInput.value) {
            this.handleDateChange(hiddenDateInput.value);
        }
    }

    /**
     * Handle date change
     * @param {string} dateString - Date in various formats (YYYY-MM-DD, DD.MM.YYYY, etc.)
     */
    handleDateChange(dateString) {
        if (!dateString) return;

        const date = this.parseDate(dateString);
        if (!date) return;

        const isSaturday = date.getDay() === 6;

        if (isSaturday) {
            this.addSaturdaySurcharge();
        } else {
            this.removeSaturdaySurcharge();
        }
    }

    /**
     * Parse date string in various formats
     * @param {string} dateString
     * @returns {Date|null}
     */
    parseDate(dateString) {
        // Try DD/MM/YYYY format (used by Delivery & Pickup app: 25/10/2025)
        const ddmmyyyyMatch = dateString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (ddmmyyyyMatch) {
            const [, day, month, year] = ddmmyyyyMatch;
            const date = new Date(year, month - 1, day);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }

        // Try German format with dots (DD.MM.YYYY)
        const germanMatch = dateString.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
        if (germanMatch) {
            const [, day, month, year] = germanMatch;
            const date = new Date(year, month - 1, day);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }

        // Try ISO format (YYYY-MM-DD)
        let date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            return date;
        }

        // Try other formats with various separators
        const parts = dateString.split(/[-/.]/);
        if (parts.length === 3) {
            // Try DD/MM/YYYY or DD-MM-YYYY
            date = new Date(parts[2], parts[1] - 1, parts[0]);
            if (!isNaN(date.getTime())) {
                return date;
            }

            // Try YYYY-MM-DD
            date = new Date(parts[0], parts[1] - 1, parts[2]);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }

        return null;
    }

    /**
     * Add Saturday surcharge to cart
     */
    async addSaturdaySurcharge() {
        if (!this.saturdaySurchargeVariantId) {
            return;
        }

        // Always check if already in cart before adding
        const alreadyInCart = await this.isSurchargeInCart();
        if (alreadyInCart) {
            this.surchargeInCart = true;
            this.showSaturdayMessage();
            return;
        }

        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: this.saturdaySurchargeVariantId,
                    quantity: 1,
                    properties: {
                        '_samstagszustellung': 'true',
                        '_aufpreis': 'true'
                    }
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            await response.json();
            this.surchargeInCart = true;
            this.showSaturdayMessage();
            this.refreshCart();

        } catch (error) {
            // Silent fail
        }
    }

    /**
     * Remove Saturday surcharge from cart
     */
    async removeSaturdaySurcharge() {
        if (!this.surchargeInCart && !await this.isSurchargeInCart()) {
            return;
        }

        try {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();

            const surchargeItem = cart.items.find(item =>
                item.variant_id === this.saturdaySurchargeVariantId ||
                (item.properties && item.properties._samstagszustellung === 'true')
            );

            if (!surchargeItem) {
                this.surchargeInCart = false;
                return;
            }

            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: surchargeItem.key,
                    quantity: 0
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            await response.json();
            this.surchargeInCart = false;
            this.refreshCart();

        } catch (error) {
            // Silent fail
        }
    }

    /**
     * Check if surcharge is currently in cart
     * @returns {Promise<boolean>}
     */
    async isSurchargeInCart() {
        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();

            const hasSurcharge = cart.items.some(item =>
                item.variant_id === this.saturdaySurchargeVariantId ||
                (item.properties && item.properties._samstagszustellung === 'true')
            );

            this.surchargeInCart = hasSurcharge;
            return hasSurcharge;

        } catch (error) {
            return false;
        }
    }

    /**
     * Show Saturday delivery message
     */
    showSaturdayMessage() {
        const message = document.querySelector('[data-saturday-message]');
        if (message) {
            message.classList.remove('hidden');
        }
    }

    /**
     * Hide Saturday delivery message
     */
    hideSaturdayMessage() {
        const message = document.querySelector('[data-saturday-message]');
        if (message) {
            message.classList.add('hidden');
        }
    }

    /**
     * Update message display based on cart content
     */
    async updateMessageDisplay() {
        const hasSurcharge = await this.isSurchargeInCart();
        if (hasSurcharge) {
            this.showSaturdayMessage();
        } else {
            this.hideSaturdayMessage();
        }
    }

    /**
     * Refresh cart display
     */
    refreshCart() {
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Update the message without reloading the page
        setTimeout(() => this.updateMessageDisplay(), 100);

        // Optionally refresh cart via Shopify's cart API
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                // Update cart count if needed
                const cartCountElements = document.querySelectorAll('[data-header-cart-count]');
                cartCountElements.forEach(el => {
                    el.textContent = cart.item_count;
                });
            })
            .catch(() => {
                // Silent fail
            });
    }
}

// Initialize the handler when DOM is ready
if (typeof window !== 'undefined') {
    window.saturdayDeliveryHandler = new SaturdayDeliveryHandler();
}